import AttachmentsUploaderBase from "components/attachments/uploader/uploader.client.base"
import uploaderConfig from "components/attachments/uploader/uploader.config.json"
import { fileTooLargeMessage, toUserFacingErrorMessage } from "components/attachments/uploader/uploader.utils"
import { Component, Prop, Watch } from "vue-property-decorator"
import vue2Dropzone from "vue2-dropzone"

// NOTE shared with the server-side compression in attachments.server.utils.ts, so both sides agree
const COMPRESSION_SKIP_BELOW_BYTES = uploaderConfig.logoCompression.skipBelowKB * 1024
const COMPRESSION_MAX_DIMENSION = uploaderConfig.logoCompression.maxDimension
const COMPRESSION_QUALITY = uploaderConfig.logoCompression.quality

type DropzoneFile = { previewElement?: HTMLElement }

@Component({
    components: {
        vueDropzone: vue2Dropzone
    }
})
export default class AttachmentsUploaderWeb extends AttachmentsUploaderBase {
    @Prop(Boolean) public clearSuccessful!: boolean
    @Prop({ type: Boolean, default: false }) public allowSingleUpload!: boolean
    public refGuid: string = Math.random().toString()
    private recentErrorMessages = new Set<string>()

    public get uploaderOptions(): UploaderOptions {
        const maxFilesize = this.maxFileSize ? this.maxFileSize : uploaderConfig.maxFileSize
        const options: UploaderOptions = {
            url: this.uploadUrl,
            createImageThumbnails: false,
            maxFilesize,
            addRemoveLinks: false,
            uploadMultiple: true,
            parallelUploads: 10,
            acceptedFiles: this.acceptedFiles ? this.acceptedFiles : uploaderConfig.allowedFileTypes,
            timeout: 120000,
            headers: { "My-Awesome-Header": "header value" },
            autoProcessQueue: !this.uploadOnSave,
            dictFileTooBig: fileTooLargeMessage(maxFilesize)
        }

        if (this.allowSingleUpload) options.maxFiles = 1
        if (this.compressImages) options.transformFile = (file, done) => this.transformFile(file, done)
        return options
    }

    public transformFile(file: File, done: (transformedFile: File | Blob) => void): void {
        // NOTE gif is skipped because drawing it to a canvas keeps only the first frame, losing animation
        const isCompressibleImage = file.type.startsWith("image/") && file.type !== "image/svg+xml" && file.type !== "image/gif"
        if (!isCompressibleImage || file.size < COMPRESSION_SKIP_BELOW_BYTES) {
            done(file)
            return
        }

        // NOTE loaded on demand so the library only ships to users who actually upload a large logo

        import("browser-image-compression")
            .then(({ default: imageCompression }) =>
                imageCompression(file, {
                    maxWidthOrHeight: COMPRESSION_MAX_DIMENSION,
                    initialQuality: COMPRESSION_QUALITY,
                    // NOTE useWebWorker:true makes this library fetch its own script from cdn.jsdelivr.net
                    // at runtime (importScripts inside the worker) - keeping it off avoids that external dependency
                    useWebWorker: false
                })
            )
            .then((compressedFile) => done(compressedFile))
            // NOTE a failed compression (or failed chunk load) still uploads the original - the server compresses it too
            .catch(() => done(file))
    }

    @Watch("uploadNow")
    public onUploadNowChange(newVal: boolean): void {
        // @ts-ignore processQueue exists on this ref
        if (newVal) this.$refs[this.refGuid].processQueue()
    }

    public success(payload: unknown, serverResponse: unknown[]): void {
        // @ts-ignore removeFile exists on this ref
        if (this.clearSuccessful) this.$refs[this.refGuid].removeFile(payload)
        this.removeLoader(payload as DropzoneFile)

        this.$emit("updated-record", serverResponse[0])
    }

    public uploadError(file: DropzoneFile, message: string | Record<string, unknown>): void {
        this.removeLoader(file)

        // @ts-ignore removeFile exists on this ref
        this.$refs[this.refGuid].removeFile(file)

        const errorMessage = toUserFacingErrorMessage(message)

        // NOTE a rejected batch (e.g. multiple oversized files) fires this callback once per file with the
        // same combined server message - suppress identical messages within the same batch so only one toast
        // shows, while files rejected for different reasons still each get their own toast
        if (this.recentErrorMessages.has(errorMessage)) return
        this.recentErrorMessages.add(errorMessage)
        setTimeout(() => {
            this.recentErrorMessages.delete(errorMessage)
        }, 0)

        this.snackEmitter.showCustomSnack({ color: "error", message: errorMessage })
    }

    private removeLoader(file: DropzoneFile): void {
        const loaderElement = file.previewElement?.querySelector(".dz-loader")
        if (loaderElement) loaderElement.remove()
    }

    public addedFileToUpload(file: DropzoneFile): void {
        const previewElement = file.previewElement
        const dropzoneComponent = this.$refs[this.refGuid] as Vue
        dropzoneComponent.$el.children[1].children[2].remove()
        const loaderElement = document.createElement("div")
        loaderElement.className = "dz-loader"
        if (previewElement) previewElement.appendChild(loaderElement)

        this.$emit("uploading", true)
    }

    public onQueueComplete(): void {
        this.$emit("uploading", false)
    }
}
